import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Link as MuiLink,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import {
  exportThresholdChargesCsvUrl,
  listThresholdCharges,
} from '@src/api/billingThreshold';

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'charged', label: 'Charged' },
  { value: 'retried_success', label: 'Retried (succeeded)' },
  { value: 'failed', label: 'Failed' },
  { value: 'paused', label: 'Paused (velocity)' },
  { value: 'pending', label: 'Pending' },
];

const OUTCOME_CHIP_COLORS = {
  pending: 'default',
  charged: 'success',
  retried_success: 'success',
  failed: 'error',
  paused: 'error',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function BillingActivityPage() {
  const user = useEffectiveUser() || {};
  const isStaff = user.is_staff || user.is_superuser;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const queryParams = useMemo(() => {
    const p = {};
    if (outcomeFilter) p.outcome = outcomeFilter;
    if (clientFilter) p.client_id = clientFilter;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [outcomeFilter, clientFilter, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listThresholdCharges(queryParams);
      const data = resp?.data;
      setRows(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    let charged = 0;
    let failed = 0;
    let totalCharged = 0;
    rows.forEach((row) => {
      if (row.outcome === 'charged' || row.outcome === 'retried_success') {
        charged += 1;
        totalCharged += Number(row.cumulative_spend || 0);
      } else if (row.outcome === 'failed' || row.outcome === 'paused') {
        failed += 1;
      }
    });
    return { charged, failed, totalCharged, count: rows.length };
  }, [rows]);

  if (!isStaff) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Staff access only.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Payment Activity</Typography>
          <Typography variant="body2" color="text.secondary">
            Every threshold-billing charge attempt across all clients
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            href={exportThresholdChargesCsvUrl(queryParams)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DownloadIcon />}
          >
            Export CSV
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary strip */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total events
              </Typography>
              <Typography variant="h6">{summary.count}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Charged
              </Typography>
              <Typography variant="h6" color="success.main">
                {summary.charged}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Failed / paused
              </Typography>
              <Typography variant="h6" color="error.main">
                {summary.failed}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total charged
              </Typography>
              <Typography variant="h6">
                {formatCurrency(summary.totalCharged)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Outcome"
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {OUTCOME_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Client ID"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              size="small"
              onClick={() => {
                setOutcomeFilter('');
                setClientFilter('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No charges match the current filters.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Triggered</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Attempt</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Threshold</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Failure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDateTime(row.triggered_at)}</TableCell>
                      <TableCell>
                        {row.client ? `#${row.client}` : '—'}
                      </TableCell>
                      <TableCell>#{row.attempt_number}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.cumulative_spend)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.threshold_at_trigger)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.outcome_display || row.outcome}
                          color={OUTCOME_CHIP_COLORS[row.outcome] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{row.invoice_payment_method || '—'}</TableCell>
                      <TableCell>
                        {row.invoice_pdf_url ? (
                          <MuiLink
                            href={row.invoice_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.invoice_number || 'PDF'}
                          </MuiLink>
                        ) : (
                          row.invoice_number || '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {row.failure_reason || ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
